import React, { useState } from 'react'

import {
  Box,
  Collapse,
  List,
  ListItemButton,
  Typography,
  IconButton,
} from '@mui/material'
import { IconChevronDown } from '@tabler/icons-react'

import { Link } from '@redwoodjs/router'
import { useLocation } from '@redwoodjs/router'

interface CollapsibleNavSectionProps {
  label: string
  icon: React.ReactElement
  children: React.ReactNode
  defaultExpanded?: boolean
  headerAction?: React.ReactNode
}

const sectionHeaderSx = {
  height: '40px',
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderRadius: '4px',
  my: 0.5,
  mx: 0,
  pl: 2,
  pr: '4px',
  opacity: 0.8,
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: (theme) => theme.palette.secondary.light,
    opacity: 1,
  },
}

const CollapsibleNavSection: React.FC<CollapsibleNavSectionProps> = ({
  label,
  icon,
  children,
  defaultExpanded = true,
  headerAction,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded)

  return (
    <Box>
      <Box
        sx={sectionHeaderSx}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          {React.cloneElement(icon, {
            size: 20,
            stroke: 2,
          } as any)}
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              color: (theme) => theme.palette.text.primary,
              marginLeft: '10px',
              fontFamily: 'Poppins',
            }}
          >
            {label}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {headerAction}
          <IconButton
            size="small"
            sx={{
              p: 0,
              width: 20,
              height: 20,
              transition: 'transform 0.2s',
              transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            }}
            onClick={(e) => {
              e.stopPropagation()
              setExpanded(!expanded)
            }}
          >
            <IconChevronDown size={16} />
          </IconButton>
        </Box>
      </Box>
      <Collapse in={expanded}>
        <List sx={{ pl: 2, py: 0 }}>{children}</List>
      </Collapse>
    </Box>
  )
}

export default CollapsibleNavSection

// Simple NavItem for use within sections
interface SimpleNavItemProps {
  label: string
  route: string
  icon: React.ReactElement
}

export const SimpleNavItem: React.FC<SimpleNavItemProps> = ({
  label,
  route,
  icon,
}) => {
  const location = useLocation()
  const isCurrentRoute = location.pathname === route

  return (
    <ListItemButton
      component={Link}
      to={route}
      sx={{
        height: '32px',
        borderRadius: '4px',
        my: 0,
        ml: '18px',
        px: '12px',
        opacity: 0.8,
        transition: 'all 0.2s ease',
        backgroundColor: isCurrentRoute
          ? (theme) => theme.palette.secondary.light
          : 'transparent',
        '&:hover': {
          backgroundColor: (theme) => theme.palette.secondary.light,
          opacity: 1,
        },
      }}
    >
      {React.cloneElement(icon, {
        size: 12,
        stroke: 2,
      } as any)}
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: isCurrentRoute ? 600 : 400,
          color: (theme) => theme.palette.text.primary,
          marginLeft: '10px',
          fontFamily: 'Poppins',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
        }}
      >
        {label}
      </Typography>
    </ListItemButton>
  )
}
